import { motion } from "framer-motion";
import {
  Heartbeat, Umbrella, Infinity as InfinityIcon, Flower, Users,
  FirstAidKit, Plus, Tooth, ArrowUpRight,
} from "@phosphor-icons/react";
import { CARRIERS } from "@/lib/api";

const LIFE = [
  { icon: Umbrella, title: "Term Life", desc: "Affordable coverage for a set period — ideal for mortgages and young families." },
  { icon: InfinityIcon, title: "Whole Life", desc: "Lifelong protection that builds guaranteed cash value over time." },
  { icon: Heartbeat, title: "Universal Life", desc: "Flexible premiums and death benefits that adapt as your life changes." },
  { icon: Flower, title: "Final Expense", desc: "Simplified whole life to cover funeral and end-of-life costs with dignity." },
];

const HEALTH = [
  { icon: Users, title: "Individual & Family Plans", desc: "ACA marketplace and private plans matched to your budget and doctors." },
  { icon: FirstAidKit, title: "Medicare Solutions", desc: "Advantage, Supplement & Part D guidance for those turning 65 and beyond." },
  { icon: Plus, title: "Supplemental Health", desc: "Accident, critical illness & hospital indemnity to fill the gaps." },
  { icon: Tooth, title: "Vision & Dental", desc: "Standalone and bundled plans that keep the whole family covered." },
];

const IMG_HEALTH =
  "https://images.unsplash.com/photo-1758691462430-81160850496c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

const reveal = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] } }),
};

function Card({ item, i, dark }) {
  const Icon = item.icon;
  return (
    <motion.div
      custom={i}
      variants={reveal}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-60px" }}
      data-testid={`service-${item.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
      className={`group rounded-xl p-6 border transition-all duration-300 ${
        dark
          ? "border-white/10 bg-white/[0.03] hover:bg-white/[0.06]"
          : "border-black/[0.07] bg-white hover:border-navy/30 hover:-translate-y-1"
      }`}
    >
      <span className={`grid place-items-center w-12 h-12 rounded-lg mb-5 transition-colors ${
        dark ? "bg-gold/15 text-gold" : "bg-navy/[0.06] text-navy group-hover:bg-navy group-hover:text-gold"
      }`}>
        <Icon weight="duotone" size={26} />
      </span>
      <h3 className={`font-heading font-bold text-lg ${dark ? "text-warm-white" : "text-navy"}`}>
        {item.title}
      </h3>
      <p className={`mt-2 text-sm leading-relaxed ${dark ? "text-light-slate" : "text-brand-slate"}`}>
        {item.desc}
      </p>
    </motion.div>
  );
}

function SectionHead({ eyebrow, title, dark }) {
  return (
    <div className="mb-10">
      <p className="eyebrow text-gold mb-4 flex items-center gap-3">
        <span className="inline-block w-8 h-px bg-gold" />
        {eyebrow}
      </p>
      <h2 className={`font-heading font-extrabold tracking-tighter text-[clamp(2rem,4.5vw,3.4rem)] leading-[1] ${dark ? "text-warm-white" : "text-navy"}`}>
        {title}
      </h2>
    </div>
  );
}

export default function Services() {
  return (
    <>
      {/* LIFE */}
      <section id="life" className="bg-warm-white py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-7">
              <SectionHead eyebrow="Life Insurance" title="Coverage that outlives the unexpected." />
            </div>
            <div className="lg:col-span-5 lg:pb-3">
              <p className="text-brand-slate leading-relaxed">
                Whether you're protecting a mortgage, replacing income, or leaving a legacy,
                we compare top-rated carriers to find the right structure and price.
              </p>
              <a
                href={CARRIERS.life} target="_blank" rel="noopener noreferrer"
                data-testid="carrier-life"
                className="mt-4 inline-flex items-center gap-2 text-navy font-semibold text-sm hover:text-gold transition-colors"
              >
                Start a life application <ArrowUpRight weight="bold" size={16} />
              </a>
            </div>
          </div>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {LIFE.map((item, i) => <Card key={item.title} item={item} i={i} />)}
          </div>
        </div>
      </section>

      {/* HEALTH */}
      <section id="health" className="bg-navy grain relative py-24 lg:py-32">
        <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="clip-frame overflow-hidden rounded-xl border border-white/10">
                <img
                  src={IMG_HEALTH}
                  alt="A doctor consulting with a patient in a clinic"
                  className="w-full h-[360px] lg:h-[440px] object-cover"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="lg:col-span-7 order-1 lg:order-2">
              <SectionHead dark eyebrow="Health Insurance" title="Care that keeps your family whole." />
              <p className="text-light-slate leading-relaxed max-w-xl">
                From ACA marketplace plans to Medicare, we translate the fine print and enroll
                you through official broker portals — no cost to you.
              </p>
              <div className="mt-8 grid sm:grid-cols-2 gap-4">
                {HEALTH.map((item, i) => <Card key={item.title} item={item} i={i} dark />)}
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href={CARRIERS.health} target="_blank" rel="noopener noreferrer" data-testid="carrier-health"
                  className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-navy font-semibold text-sm hover:brightness-110 transition-all">
                  Enroll via HealthSherpa <ArrowUpRight weight="bold" size={16} />
                </a>
                <a href={CARRIERS.medicare} target="_blank" rel="noopener noreferrer" data-testid="carrier-medicare"
                  className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-warm-white font-semibold text-sm hover:bg-white/5 transition-all">
                  Medicare resources <ArrowUpRight weight="bold" size={16} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
